import classNames from 'classnames';
import { object, string } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  ValidFirstLineOfAddress,
  ValidPostcode,
} from '@javascript/lib/regularExpressions';
import LocationFinder from '@javascript/components/application/input/locationFinder';
import { useState } from 'react';

const formSchema = object().shape({
  address1: string()
    .matches(ValidFirstLineOfAddress, 'Please enter your address')
    .required('Please enter your address'),
  address2: string(),
  city: string().required('Please enter your city'),
  postcode: string()
    .matches(ValidPostcode, 'Please enter your postcode')
    .required('Please enter your postcode'),
});

interface FormInput {
  address1: string;
  city: string;
  postcode: string;
  address2?: string | undefined;
}

interface AddressViewProps {
  customerDetails: {
    address1: string;
    address2: string;
    city: string;
    postcode: string;
  };
  customerLocation: string;
  loading: boolean;
  sendEnquiry: (customerDetails: object) => void;
  updateCustomerDetails: (
    details: {
      address1: string;
      address2: string | undefined;
      city: string;
      postcode: string;
    },
    callback: (customerDetails: object) => void,
  ) => void;
}

const AddressView = ({
  customerDetails,
  loading,
  sendEnquiry,
  updateCustomerDetails,
}: AddressViewProps): JSX.Element => {
  const [manualInput, setManualInput] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    trigger,
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: customerDetails,
    resolver: yupResolver(formSchema),
  });

  const onLocationSelect = (
    _formattedAddress: string,
    address1: string,
    address2: string,
    city: string,
    postcode: string,
  ) => {
    // Reset the form to immediately clear any previous errors
    reset();

    setValue('address1', address1);
    setValue('address2', address2);
    setValue('city', city);
    setValue('postcode', postcode);

    //trigger validation
    trigger();
  };

  const onSubmit: SubmitHandler<FormInput> = (data) => {
    updateCustomerDetails(
      {
        address1: data.address1,
        address2: data.address2,
        city: data.city,
        postcode: data.postcode,
      },
      sendEnquiry,
    );
  };

  const addressEntered = !!watch('postcode');

  const errorsPresent = Object.keys(errors).length > 0;

  const lookupError = !addressEntered && !manualInput && errorsPresent;

  const showAddressCard =
    addressEntered && !errorsPresent && !isDirty && !manualInput;

  const displayInputFields = (addressEntered && errorsPresent) || isDirty;

  return (
    <div className="db-modal__content">
      <form onSubmit={handleSubmit(onSubmit)}>
        <section className="db-section ac-animation--fade-in-from-right">
          <p className="db-question">Your billing address</p>
          <div className="ch-overflow--auto">
            <div
              className={classNames(
                'ch-form__group',
                lookupError && 'ch-form__group--error',
              )}
            >
              <label className="ch-label" htmlFor="location">
                Your address
              </label>
              <p className="ch-form__control-hint">
                Start typing to find your address
              </p>
              {lookupError && (
                <span className="ch-form__control-validation">
                  Please enter your address
                </span>
              )}
              <LocationFinder
                error={lookupError}
                id="location"
                isFullAddress
                onLocationSelect={onLocationSelect}
              />
            </div>
            {!manualInput && !addressEntered && (
              <button
                className="ch-btn ch-btn--link ch-pl--0 ch-mb--3"
                onClick={() => setManualInput(true)}
                type="button"
              >
                Enter your address manually
              </button>
            )}
            {showAddressCard && (
              <div className="db-address__card ch-card ch-rounded--md">
                <div className="ch-fs--3 ch-fw--500 ch-card__content">
                  <p className="ch-mb--0">{watch('address1')}</p>
                  <p className="ch-mb--0">{watch('address2')}</p>
                  <p className="ch-mb--0">{watch('city')}</p>
                  <p className="ch-mb--0">{watch('postcode')}</p>
                </div>
                <button
                  className="ch-btn ch-btn--link ch-mb--3"
                  onClick={() => setManualInput(true)}
                  type="button"
                >
                  Edit address
                </button>
              </div>
            )}
            {(manualInput || displayInputFields) && (
              <>
                <hr className="ch-mb--4 ch-bg--grey-3" />
                <div
                  className={classNames(
                    'ch-form__group',
                    errors.address1 && 'ch-form__group--error',
                  )}
                >
                  <label className="ch-label" htmlFor="address1">
                    Address line 1
                  </label>
                  {errors.address1 && (
                    <span className="ch-form__control-validation">
                      {errors.address1.message}
                    </span>
                  )}
                  <input
                    className="ch-form__control"
                    id="address1"
                    type="text"
                    {...register('address1')}
                  />
                </div>
                <div className={classNames('ch-form__group')}>
                  <label className="ch-label" htmlFor="address2">
                    Address line 2{' '}
                    <span className="ch-fw--400 ch-color--grey-6">
                      (Optional)
                    </span>
                  </label>
                  <input
                    className="ch-form__control"
                    id="address2"
                    type="text"
                    {...register('address2')}
                  />
                </div>
                <div
                  className={classNames(
                    'ch-form__group',
                    errors.city && 'ch-form__group--error',
                  )}
                >
                  <label className="ch-label" htmlFor="city">
                    Town/city
                  </label>
                  {errors.city && (
                    <span className="ch-form__control-validation">
                      {errors.city.message}
                    </span>
                  )}
                  <input
                    className="ch-form__control"
                    id="city"
                    type="text"
                    {...register('city')}
                  />
                </div>
                <div
                  className={classNames(
                    'ch-form__group',
                    errors.postcode && 'ch-form__group--error',
                  )}
                >
                  <label className="ch-label" htmlFor="postcode">
                    Postcode
                  </label>
                  {errors.postcode && (
                    <span className="ch-form__control-validation">
                      {errors.postcode?.message}
                    </span>
                  )}
                  <input
                    className="ch-form__control"
                    id="postcode"
                    type="text"
                    {...register('postcode')}
                  />
                </div>
              </>
            )}
          </div>
        </section>
        <section className="db-section db-section--cta">
          <button
            className={classNames('ch-btn ch-btn--block ch-btn--cta', {
              'ac-btn--loading': loading,
            })}
            type="submit"
          >
            Next: Payment
          </button>
        </section>
      </form>
    </div>
  );
};

export default AddressView;
